import { useState } from 'react';

import { Navigate } from '@tanstack/react-router';

import { AlertTriangle, Check, CreditCard } from 'lucide-react';

import { Button, cn } from '@src/components/ui';
import { PLAN_FEATURES, PLAN_ORDER } from '@src/constants';
import { useActiveOrg, usePermissions } from '@src/context/hooks';
import { formatDate, formatPrice } from '@src/corelib/utils/formatUtils';
import { trialDaysLeft } from '@src/corelib/utils/subscriptionUtils';
import {
  useBillingCancelMutation,
  useBillingInterchangeMutation,
  useBillingPaymentMethodMutation,
  useBillingPlanMutation,
  useBillingQuery,
  useBillingSubscribeMutation,
  useMineQuery,
  useOptionLabels,
} from '@src/query';

import { formatScanCredits } from '../Inventory/photoIntake/credits';
import { Toaster, useToasts } from '../Listings/useToasts';
import BillingPaymentModal from './BillingPaymentModal';
import { getStripePromise, isStripeProvider } from './billingProvider';
import { cancelNotice, formatCardLine, renewalNotice } from './billingStatus';
import { ConfirmDialog, firstApiError, Switch } from './controls';
import PageHeader from './PageHeader';


/** Returns a stable `(plan) => label` resolver backed by the metadata-options
 * hook (#364), preserving the prior capitalize/"Free" fallback. MUST be called
 * at component top level. */
function usePlanLabel() {
  const labels = useOptionLabels('subscription_plan');
  return (plan) => labels[plan] || (plan ? `${plan[0].toUpperCase()}${plan.slice(1)}` : 'Free');
}


/** Top card: state-aware banner for trialing / active / expired / past_due.
 * On the Stripe provider it also surfaces the card on file, the renewal date, and
 * a "Cancels on {date}" notice; the simulated path renders exactly as before. */
function StatusCard({ subscription, daysLeft, billing }) {
  const planLabel = usePlanLabel();
  const status = subscription?.effective_status;
  const stripeMode = isStripeProvider(billing);
  const cardLine = stripeMode ? formatCardLine(billing?.card) : null;
  const cancels = stripeMode ? cancelNotice(subscription) : null;
  const renews = stripeMode && status === 'active' ? renewalNotice(subscription) : null;

  const CONFIG = {
    trialing: {
      frame: 'bg-primary-soft border-primary/30',
      iconBox: 'bg-brand-gradient text-white',
      icon: CreditCard,
      title: daysLeft === 0
        ? 'Free trial ended'
        : `Free trial — ${daysLeft} day${daysLeft === 1 ? '' : 's'} left`,
      subtitle: 'Everything unlocked. Choose a plan to keep going.',
    },
    active: {
      frame: 'bg-success-soft border-success/30',
      iconBox: 'bg-success-soft text-success',
      icon: Check,
      title: `${planLabel(subscription?.plan)} plan — active`,
      subtitle: 'Your subscription is current. Manage your plan and add-ons below.',
    },
    expired: {
      frame: 'bg-danger-soft border-danger/30',
      iconBox: 'bg-danger-soft text-danger',
      icon: AlertTriangle,
      title: 'Subscription expired',
      subtitle: 'Reads still work, but changes are locked. Choose a plan to restore full access.',
    },
    past_due: {
      frame: 'bg-danger-soft border-danger/30',
      iconBox: 'bg-danger-soft text-danger',
      icon: AlertTriangle,
      title: 'Payment past due',
      subtitle: 'Changes are locked until billing is updated. Re-activate a plan below.',
    },
  };

  const config = CONFIG[status] || CONFIG.active;
  const Icon = config.icon;

  return (
    <div
      className={cn(
        'flex flex-col gap-2.5 rounded-xl border px-[18px] py-4',
        config.frame,
      )}
    >
      <div className="flex items-center gap-3.5">
        <span className={cn('grid size-[38px] shrink-0 place-items-center rounded-lg', config.iconBox)}>
          <Icon className="size-[18px]" />
        </span>

        <span className="flex min-w-0 flex-col gap-0.5">
          <span className="text-card-title font-semibold text-ink">{config.title}</span>
          <span className="text-table text-ink-muted">{config.subtitle}</span>

          {/* Stripe-only meta: card on file + renewal date. Absent on the
              simulated provider so its banner is byte-for-byte unchanged. */}
          {(cardLine || renews)
            ? (
              <span className="mt-0.5 flex flex-wrap items-center gap-x-2.5 gap-y-0.5 text-xs text-ink-subtle">
                {cardLine ? <span>{cardLine}</span> : null}
                {cardLine && renews ? <span aria-hidden>·</span> : null}
                {renews ? <span>{renews}</span> : null}
              </span>
            )
            : null}
        </span>
      </div>

      {cancels
        ? (
          <span className="rounded-lg bg-warning-soft px-3 py-1.5 text-xs font-medium text-warning">
            {cancels}
          </span>
        )
        : null}
    </div>
  );
}

StatusCard.displayName = 'StatusCard';


function PlanCard({ plan, price, seatCap, isCurrent, popular, disabled, onSelect }) {
  const planLabel = usePlanLabel();
  return (
    <div
      className={cn(
        'flex flex-1 flex-col gap-3 rounded-lg bg-surface-1 p-[18px]',
        popular ? 'border-2 border-primary' : 'border border-hairline',
      )}
    >
      <span className="flex items-center justify-between">
        <span className="text-card-title font-semibold text-ink">{planLabel(plan)}</span>

        {popular
          ? (
            <span className="rounded-full bg-primary-soft px-2 py-0.5 text-[10px] font-semibold text-primary">
              Popular
            </span>
          )
          : null}
      </span>

      <span className="flex items-end gap-[3px]">
        <span
          className={cn(
            'text-[28px] font-bold leading-none tracking-[-1px]',
            popular ? 'text-primary' : 'text-ink',
          )}
        >
          {formatPrice(price)}
        </span>

        <span className="text-table text-ink-subtle">/mo</span>
      </span>

      <span className="text-table font-medium text-ink-muted">{`${seatCap} seats`}</span>

      <span className="flex flex-1 flex-col gap-[7px]">
        {(PLAN_FEATURES[plan] || []).map((feature) => (
          <span key={feature} className="flex items-center gap-[7px]">
            <Check className="size-3.5 shrink-0 text-success" strokeWidth={2.5} />
            {/* `{seats}` is filled from the server seat_cap so the bullet never hardcodes the count. */}
            <span className="text-table text-ink-muted">{feature.replace('{seats}', seatCap)}</span>
          </span>
        ))}
      </span>

      {isCurrent
        ? (
          <span
            className={cn(
              'flex h-10 items-center justify-center rounded-lg border border-hairline-strong',
              'bg-surface-2 text-sm font-semibold text-ink-muted',
            )}
          >
            Current plan
          </span>
        )
        : (
          <Button
            type="button"
            disabled={disabled}
            className="h-10 text-sm"
            variant={popular ? 'primary' : 'secondary'}
            onClick={onSelect}
          >
            {popular ? `Choose ${planLabel(plan)}` : 'Select'}
          </Button>
        )}
    </div>
  );
}

PlanCard.displayName = 'PlanCard';


const INVOICE_COLUMNS = [
  ['Invoice', 'flex-1'],
  ['Date', 'w-28'],
  ['Plan', 'w-24'],
  ['Amount', 'w-20'],
  ['Status', 'w-24'],
];


/** Title-cases a Stripe invoice status ("open" -> "Open"). Unpaid rows previously
 * rendered the raw lowercase status next to a title-cased "Paid". */
function invoiceStatusLabel(status) {
  if (!status) {
    return '—';
  }
  return `${status[0].toUpperCase()}${status.slice(1).replace(/_/g, ' ')}`;
}


function InvoicesTable({ invoices }) {
  const planLabel = usePlanLabel();
  if (invoices.length === 0) {
    return (
      <span
        className={cn(
          'rounded-lg border border-hairline bg-surface-1 px-4 py-5 text-center text-table text-ink-subtle',
        )}
      >
        No invoices yet.
      </span>
    );
  }

  return (
    <div className="overflow-hidden rounded-lg border border-hairline bg-surface-1">
      <div className="flex h-9 items-center gap-3 border-b border-hairline px-4">
        {INVOICE_COLUMNS.map(([label, width]) => (
          <span
            key={label}
            className={cn('text-[10px] font-semibold uppercase tracking-[0.3px] text-ink-subtle', width)}
          >
            {label}
          </span>
        ))}
      </div>

      {invoices.map((invoice) => (
        <div
          key={invoice.id}
          className="flex items-center gap-3 border-b border-hairline px-4 py-2.5 last:border-0"
        >
          <span className="flex-1 truncate text-table font-medium text-ink">{invoice.number}</span>
          <span className="w-28 text-table text-ink-muted">{formatDate(invoice.issued_at, { withYear: true })}</span>
          <span className="w-24 text-table text-ink-muted">{planLabel(invoice.plan)}</span>
          <span className="w-20 text-table font-medium text-ink">{formatPrice(invoice.amount)}</span>

          <span className="w-24">
            <span
              className={cn(
                'rounded-full px-2 py-0.5 text-xs font-semibold',
                invoice.status === 'paid' ? 'bg-success-soft text-success' : 'bg-warning-soft text-warning',
              )}
            >
              {invoiceStatusLabel(invoice.status)}
            </span>
          </span>
        </div>
      ))}
    </div>
  );
}

InvoicesTable.displayName = 'InvoicesTable';


/** Billing & plan (#160, #161, .pen h0moNd): state-aware status card, plan
 * picker (simulated checkout), interchange add-on, seats, demo payment method,
 * invoices. Owner only — other roles are redirected. Demo billing — no
 * payment processor. */
function Billing() {
  const { toasts, push } = useToasts();
  const planLabel = usePlanLabel();
  // Mine query: its cache key lets plan/cancel mutations invalidate the org, and its
  // refetch refreshes the app-shell SubscriptionBanner (fed by useActiveOrg → this same
  // /sellers/mine/ cache) after a webhook-driven Stripe activation, which otherwise left
  // the global "Free trial" banner stale until a manual reload.
  const { queryKey: mineKey, refetch: refetchMine } = useMineQuery();
  const { seller } = useActiveOrg();
  const { canBilling: isOwner } = usePermissions();

  const billingQuery = useBillingQuery(seller?.id, { enabled: Boolean(seller) && isOwner });
  const queryKey = billingQuery.queryKey;

  const [pendingPlan, setPendingPlan] = useState(null);
  // Stripe checkout state (M34, provider-gated). `payment` holds the in-flight
  // subscribe attempt ({ plan, clientSecret }) that mounts the Elements modal;
  // `polling` covers the webhook-driven activation wait after the card confirms;
  // `misconfig` shows an inline error when the provider is Stripe but no
  // publishable key is configured (so we never mount a blank Elements form).
  const [payment, setPayment] = useState(null);
  const [polling, setPolling] = useState(false);
  const [misconfig, setMisconfig] = useState(false);
  // Manage flows (M34, Stripe only): `updateCard` holds the in-flight SetupIntent
  // ({ clientSecret }) that mounts the Elements modal in mode="setup";
  // `pendingCancel` gates the cancel confirm dialog.
  const [updateCard, setUpdateCard] = useState(null);
  const [pendingCancel, setPendingCancel] = useState(false);

  const planMutation = useBillingPlanMutation();
  const subscribeMutation = useBillingSubscribeMutation();
  const interchangeMutation = useBillingInterchangeMutation();
  const paymentMethodMutation = useBillingPaymentMethodMutation();
  const cancelMutation = useBillingCancelMutation();

  if (!seller) {
    return null;
  }

  if (!isOwner) {
    return <Navigate replace to="/app/settings" />;
  }

  const billing = billingQuery.data;
  const subscription = billing?.subscription;
  const seats = billing?.seats;
  const plans = billing?.plans || [];
  const invoices = billing?.invoices || [];
  const currentPlan = subscription?.plan;
  const daysLeft = trialDaysLeft(subscription?.trial_ends_at);
  const interchangeOn = Boolean(subscription?.interchange_addon);
  const interchangePrice = billing?.interchange_addon_price;

  const planByKey = Object.fromEntries(plans.map((entry) => [entry.plan, entry]));

  const stripeMode = isStripeProvider(billing);
  // Runtime publishable key (empty unless Stripe is the live provider). Falls back
  // to the build-time bundle value inside `getStripePromise`.
  const publishableKey = billing?.stripe_publishable_key;
  const busySelecting = planMutation.isPending || subscribeMutation.isPending || polling;

  // Poll the billing query until the webhook flips the subscription to active
  // (the subscribe mutation never invalidates optimistically — Stripe is the
  // authority for activation). Bounded to ~15s; if it hasn't landed we close the
  // modal with an advisory rather than blocking the UI on Stripe's retry timing.
  const pollActivation = async (plan) => {
    setPolling(true);
    for (let attempt = 0; attempt < 10; attempt += 1) {
      await new Promise((resolve) => {
        setTimeout(resolve, 1500); 
      });
      const { data } = await billingQuery.refetch();
      if (data?.subscription?.effective_status === 'active') {
        // Also refresh the org cache so the app-shell SubscriptionBanner drops the
        // "Free trial" pill immediately instead of lingering until the next reload.
        await refetchMine();
        setPolling(false);
        setPayment(null);
        push(`Switched to ${planLabel(plan)}`);
        return;
      }
    }
    setPolling(false);
    setPayment(null);
    push('Payment received — activation is taking a moment. It will update shortly.', 'warning');
  };

  // Plan CTA. Simulated billing keeps the one-click confirm dialog; Stripe opens
  // the embedded Elements checkout after the server issues a client_secret.
  const selectPlan = (plan) => {
    if (!stripeMode) {
      setPendingPlan(plan);
      return;
    }
    if (!getStripePromise(publishableKey)) {
      setMisconfig(true);
      return;
    }
    setMisconfig(false);
    subscribeMutation.mutate(
      { sellerId: seller.id, plan },
      {
        onSuccess: (data) => {
          if (data?.client_secret) {
            setPayment({ plan, clientSecret: data.client_secret });
          }
          else {
            // Already-subscribed / plan-change path returns no secret — the change
            // lands via webhook, so poll for the flip.
            pollActivation(plan);
          }
        },
        onError: (error) => push(
          firstApiError(error?.response?.data, 'plan') || 'Could not start checkout',
          'danger',
        ),
      },
    );
  };

  const confirmPlan = () => {
    const plan = pendingPlan;
    planMutation.mutate(
      { sellerId: seller.id, plan, queryKey, mineKey },
      {
        onSuccess: () => {
          setPendingPlan(null);
          push(`Switched to ${planLabel(plan)}`);
        },
        onError: (error) => {
          setPendingPlan(null);
          push(firstApiError(error?.response?.data, 'plan') || 'Could not change plan', 'danger');
        },
      },
    );
  };

  const toggleInterchange = () => {
    interchangeMutation.mutate(
      { sellerId: seller.id, enabled: !interchangeOn, queryKey, mineKey },
      { onError: () => push('Could not update add-on', 'danger') },
    );
  };

  // Update card (Stripe): fetch a SetupIntent client_secret, then open the
  // Elements modal in mode="setup". Refetch the billing query on completion so
  // the new brand/last4 lands on the StatusCard.
  const openUpdateCard = () => {
    if (!getStripePromise(publishableKey)) {
      setMisconfig(true);
      return;
    }
    setMisconfig(false);
    paymentMethodMutation.mutate(
      { sellerId: seller.id },
      {
        onSuccess: (data) => {
          if (data?.client_secret) {
            setUpdateCard({ clientSecret: data.client_secret });
          }
        },
        onError: (error) => push(
          firstApiError(error?.response?.data) || 'Could not start card update',
          'danger',
        ),
      },
    );
  };

  const onCardUpdated = async () => {
    setUpdateCard(null);
    await billingQuery.refetch();
    push('Card updated');
  };

  // Cancel plan (Stripe): the server schedules cancellation at period end and
  // returns the refreshed payload, whose `cancel_at_period_end` drives the banner.
  const confirmCancel = () => {
    cancelMutation.mutate(
      { sellerId: seller.id, queryKey, mineKey },
      {
        onSuccess: () => {
          setPendingCancel(false);
          push('Your plan will cancel at the end of the billing period');
        },
        onError: (error) => {
          setPendingCancel(false);
          push(firstApiError(error?.response?.data) || 'Could not cancel plan', 'danger');
        },
      },
    );
  };

  return (
    <div className="flex flex-1 flex-col gap-5 lg:p-7">
      <PageHeader title="Billing & plan" subtitle="Manage your subscription and add-ons." />

      <div className="flex w-full flex-col gap-[18px] px-4 pb-8 lg:px-0">
        {billingQuery.isPending
          ? <span className="py-10 text-center text-table text-ink-subtle">Loading billing…</span>
          : billingQuery.isError
            ? <span className="py-10 text-center text-table text-danger">Could not load billing.</span>
            : (
              <>
                <StatusCard billing={billing} daysLeft={daysLeft} subscription={subscription} />
                {/* Plans */}
                <span className="text-xs font-semibold uppercase tracking-[0.5px] text-ink-subtle">Plans</span>

                <div className="flex flex-col gap-3.5 lg:flex-row">
                  {PLAN_ORDER.map((plan) => {
                    const entry = planByKey[plan];
                    if (!entry) {
                      return null;
                    }
                    return (
                      <PlanCard
                        key={plan}
                        plan={plan}
                        price={entry.price}
                        seatCap={entry.seat_cap}
                        disabled={busySelecting}
                        isCurrent={plan === currentPlan}
                        popular={plan === 'shop'}
                        onSelect={() => selectPlan(plan)}
                      />
                    );
                  })}
                </div>

                <span className="text-xs text-ink-tertiary">
                  {stripeMode
                    ? 'Secure checkout — card details are handled by Stripe and never touch our servers.'
                    : 'Demo billing — selecting a plan activates instantly with no payment processor.'}
                </span>

                {misconfig
                  ? (
                    <span className="text-xs font-medium text-danger">
                      Card checkout is unavailable — the payment provider is not configured. Contact support.
                    </span>
                  )
                  : null}

                {/* Add-ons */}
                <span className="text-xs font-semibold uppercase tracking-[0.5px] text-ink-subtle">
                  Add-ons
                </span>

                <div className="flex items-center gap-3 rounded-lg border border-hairline bg-surface-1 px-4 py-3">
                  <span className="flex min-w-0 flex-1 flex-col gap-0.5">
                    <span className="text-sm font-semibold text-ink">Fitment data (interchange)</span>

                    <span className="text-xs text-ink-subtle">
                      Unlock &apos;what else this part fits&apos; suggestions
                    </span>
                  </span>

                  {/* Server-driven price (billing.interchange_addon_price) — no hardcoded amount. */}
                  <span className="text-table font-semibold text-ink-muted">{`+${formatPrice(interchangePrice)}/mo`}</span>

                  <Switch
                    checked={interchangeOn}
                    disabled={interchangeMutation.isPending}
                    label={`${interchangeOn ? 'Disable' : 'Enable'} interchange add-on`}
                    onChange={toggleInterchange}
                  />
                </div>

                {/* AI scan credits (M27 #451). Read-only: the balance is DERIVED from
                    the plan (PLAN_AI_CREDITS) plus any purchased packs, so there is no
                    toggle here — changing plan is what changes the allowance. Shown to
                    the owner even on an unentitled plan, because "what would I get"
                    is the question the Billing page exists to answer. */}
                <div
                  className={cn(
                    'flex items-center gap-3 rounded-lg border border-hairline bg-surface-1 px-4 py-3',
                  )}
                >
                  <span className="flex min-w-0 flex-1 flex-col gap-0.5">
                    <span className="text-sm font-semibold text-ink">AI photo-intake scans</span>

                    <span className="text-xs text-ink-subtle">
                      {subscription?.ai_intake_enabled
                        ? 'Photograph a part, we read the label and prefill the draft. '
                        + 'A scan that finds nothing is free.'
                        : 'Included with the Shop and Pro plans — photograph a part and we '
                          + 'prefill the draft from the label.'}
                    </span>
                  </span>

                  <span className="shrink-0 text-table font-semibold text-ink-muted">
                    {formatScanCredits(subscription) || 'Not included'}
                  </span>
                </div>

                {/* Seats */}
                {seats
                  ? (
                    <div
                      className={cn(
                        'flex flex-wrap items-center gap-2.5 rounded-lg border border-hairline',
                        'bg-surface-1 px-4 py-3',
                      )}
                    >
                      <span className="text-sm font-semibold text-ink">
                        {`${seats.used} of ${seats.cap} seats used`}
                      </span>

                      <span className="text-table text-ink-subtle">{`${planLabel(currentPlan)} plan`}</span>
                    </div>
                  )
                  : null}

                {/* Payment method (demo placeholder) */}
                <span className="text-xs font-semibold uppercase tracking-[0.5px] text-ink-subtle">
                  Payment method
                </span>

                <div className="flex items-center gap-3 rounded-lg border border-hairline bg-surface-1 px-4 py-3.5">
                  <span
                    className={cn(
                      'grid size-[38px] shrink-0 place-items-center rounded-lg bg-surface-2 text-ink-subtle',
                    )}
                  >
                    <CreditCard className="size-[18px]" />
                  </span>

                  <span className="flex min-w-0 flex-1 flex-col gap-0.5">
                    {stripeMode
                      ? (
                        <>
                          <span className="text-sm font-semibold text-ink">
                            {formatCardLine(billing?.card) || 'No card on file'}
                          </span>

                          <span className="text-xs text-ink-subtle">
                            Card details are handled by Stripe and never touch our servers.
                          </span>
                        </>
                      )
                      : (
                        <>
                          <span className="text-sm font-semibold text-ink">No card on file</span>

                          <span className="text-xs text-ink-subtle">
                            Demo billing — no payment processor connected.
                          </span>
                        </>
                      )}
                  </span>

                  {stripeMode
                    ? (
                      <Button
                        type="button"
                        variant="secondary"
                        className="h-9 shrink-0 text-sm"
                        onClick={openUpdateCard}
                        loading={paymentMethodMutation.isPending}
                      >
                        Update card
                      </Button>
                    )
                    : null}
                </div>

                {/* Cancel plan (Stripe only) — hidden once cancellation is already
                    scheduled (the StatusCard banner then shows the end date). */}
                {stripeMode
                  && subscription?.effective_status === 'active'
                  && !subscription?.cancel_at_period_end
                  ? (
                    <Button
                      type="button"
                      variant="ghost"
                      className="h-8 self-start px-0 text-xs font-medium text-danger hover:underline"
                      onClick={() => setPendingCancel(true)}
                    >
                      Cancel plan
                    </Button>
                  )
                  : null}

                {/* Invoices */}
                <span className="text-xs font-semibold uppercase tracking-[0.5px] text-ink-subtle">
                  Invoices
                </span>

                <InvoicesTable invoices={invoices} />
              </>
            )}
      </div>

      {pendingPlan
        ? (
          <ConfirmDialog
            variant="primary"
            busy={planMutation.isPending}
            title={`Switch to ${planLabel(pendingPlan)}?`}
            confirmLabel="Activate"
            onConfirm={confirmPlan}
            onCancel={() => setPendingPlan(null)}
            body="Demo billing — this activates instantly with no payment processor. You can change plans anytime."
          />
        )
        : null}

      {payment
        ? (
          <BillingPaymentModal
            publishableKey={publishableKey}
            clientSecret={payment.clientSecret}
            mode="subscribe"
            onClose={() => setPayment(null)}
            onDone={() => pollActivation(payment.plan)}
          />
        )
        : null}

      {updateCard
        ? (
          <BillingPaymentModal
            publishableKey={publishableKey}
            clientSecret={updateCard.clientSecret}
            mode="setup"
            onDone={onCardUpdated}
            onClose={() => setUpdateCard(null)}
          />
        )
        : null}

      {pendingCancel
        ? (
          <ConfirmDialog
            variant="danger"
            title="Cancel your plan?"
            busy={cancelMutation.isPending}
            icon={AlertTriangle}
            onConfirm={confirmCancel}
            confirmLabel="Cancel plan"
            onCancel={() => setPendingCancel(false)}
            body={'Your subscription stays active until the end of the current billing period, then it will not '
              + 'renew. You can resubscribe anytime.'}
          />
        )
        : null}

      <Toaster toasts={toasts} />
    </div>
  );
}

Billing.displayName = 'Billing';
export default Billing;
